import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import translations, { PROVIDER_LANGUAGES } from "./providerTranslations";

const ProviderLanguageContext = createContext(null);

const readPreferredLang = () => {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const code = user?.preferred_language;
        return translations[code] ? code : "eng";
    } catch {
        return "eng";
    }
};

export function ProviderLanguageProvider({ children }) {
    // The user's saved preference; `lang` is what the page currently renders in.
    const [preferredLang, setPreferredLang] = useState(readPreferredLang);
    const [lang, setLang] = useState(readPreferredLang);

    const toggleLang = useCallback(() => {
        setLang((prev) => (prev === "eng" ? preferredLang : "eng"));
    }, [preferredLang]);

    // Call after login or after the profile language is changed.
    const syncPreferredLang = useCallback(() => {
        const next = readPreferredLang();
        setPreferredLang(next);
        setLang(next);
    }, []);

    const t = useCallback(
        (key) => translations[lang]?.[key] ?? translations.eng[key] ?? key,
        [lang]
    );

    const value = useMemo(
        () => ({
            lang,
            preferredLang,
            // Nothing to switch to when the preference is already English.
            canToggle: preferredLang !== "eng",
            toggleLang,
            syncPreferredLang,
            t,
        }),
        [lang, preferredLang, toggleLang, syncPreferredLang, t]
    );

    return (
        <ProviderLanguageContext.Provider value={value}>
            {children}
        </ProviderLanguageContext.Provider>
    );
}

export function useProviderLanguage() {
    const ctx = useContext(ProviderLanguageContext);
    if (!ctx) {
        throw new Error("useProviderLanguage must be used within ProviderLanguageProvider");
    }
    return ctx;
}

export { PROVIDER_LANGUAGES };
